import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import ChevronRightIcon from '@/assets/icons/chevron-right.svg';
import CreditCardIcon from '@/assets/icons/credit-card.svg';
import ErrorIcon from '@/assets/icons/exclamation-circle.svg';
import LocationMarkerIcon from '@/assets/icons/location-marker.svg';
import { ClockIcon } from '@/components/icons/clock';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { useJobConstants } from './use-job-constants';

interface SummaryViewProps {
  opening: OpeningDetailResponse;
  payDisplay: string | null;
  toggleExpanded: () => void;
}

export function SummaryView({
  opening,
  payDisplay,
  toggleExpanded,
}: SummaryViewProps) {
  const intl = useIntl();
  const { getJobHours } = useJobConstants();
  const { hookForm } = useJobPostingContext();
  const {
    formState: { errors },
    getValues,
  } = hookForm;

  const postingTitle = getValues(`jobDetails.${opening.external_id}.title`);
  const hasError = errors?.jobDetails?.[opening.external_id];

  return (
    <div
      className={clsx(
        'border-fx-neutral-200 mx-4 mb-2 flex items-center justify-between gap-2 rounded-lg border bg-white py-6 px-12 shadow-sm md:w-[720px]',
        hasError && 'border-fx-rose',
      )}
    >
      <div className="flex flex-col items-start gap-2">
        <Typography variant="body2">{postingTitle}</Typography>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4">
          {(opening.location_address || opening.remote) && (
            <div className="flex items-center gap-1 overflow-hidden">
              <img
                src={LocationMarkerIcon}
                alt="Location Marker"
                className="shrink-0"
              />
              <Typography variant="subtitle1" className="truncate">
                {opening.location_address || (opening.remote && 'Remote')}
              </Typography>
            </div>
          )}

          {payDisplay && (
            <div className="flex items-center gap-1">
              <img src={CreditCardIcon} alt="Credit Card" />
              <Typography variant="subtitle1">{payDisplay}</Typography>
            </div>
          )}

          {opening.job_hours && (
            <div className="flex items-center gap-1">
              <ClockIcon />
              <Typography variant="subtitle1">
                {getJobHours(opening.job_hours)}
              </Typography>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 justify-self-end">
        {hasError && (
          <Tooltip
            content={intl.formatMessage({
              defaultMessage: 'Job description cannot be empty or incomplete.',
              description: 'Error message for job description',
            })}
          >
            <img src={ErrorIcon} alt="Error" />
          </Tooltip>
        )}
        <Button
          type="button"
          variant="icon"
          size="icon"
          className="hover:!bg-fx-neutral-100 -mr-6"
          onClick={toggleExpanded}
        >
          <img src={ChevronRightIcon} alt="Chevron right" />
        </Button>
      </div>
    </div>
  );
}

SummaryView.Skeleton = function SummaryViewSkeleton() {
  return (
    <div className="border-fx-neutral-200 mb-2 flex w-[720px] items-center justify-between gap-2 rounded-lg border bg-white py-6 pl-12 pr-6 shadow-sm">
      <div className="flex flex-col items-start gap-2">
        <Skeleton className="h-5 w-[240px]" />

        <div className="mt-1 flex w-full items-center gap-4">
          <Skeleton className="h-4 w-[320px]" />
          <Skeleton className="h-4 w-[80px]" />
          <Skeleton className="h-4 w-[80px]" />
        </div>
      </div>
      <img src={ChevronRightIcon} alt="Chevron right" />
    </div>
  );
};
